import React, { useState } from 'react';
import useWizardTree from '../../../../Hooks/useWizardTree';
import { useEffectAsync } from '../../../../helpers';
import Loading from '../../../../Components/Loading';
import MagicForm from '../../../../Components/Form/MagicForm';
import useDynamicWizard from '../../Hooks/useDynamicWizard';
import { ItemScheme } from '../../../../Components/Form/MagicFormItem';
// @ts-ignore
import styles from './DynamicForm.module.scss';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useTranslation from '../../../../Hooks/useTranslation';
import useNotification from '../../../../Hooks/useNotification.hook';
import ScrollContainer from '../../../Base/Components/ScrollContainer';

interface Props {
	wizardKey: string;
	setIsLoading: (isLoading: boolean) => void;
	patientId: string;
	onSuccess?: (formKey: string) => void;
}

interface WizardSettings {
	showCharCounter?: boolean;
	disabled?: boolean;
	submitButtonTitle?: string;
}

const DynamicForm: React.FC<Props> = ({
	wizardKey,
	patientId,
	setIsLoading,
	onSuccess,
}) => {
	const [scheme, setScheme] = useState<ItemScheme[]>([]);
	const [defaultValues, setDefaultValues] = useState<{}>({});
	const [isWizardLoading, setIsWizardLoading] = useState(false);
	const [wizardSettings, setWizardSettings] = useState<WizardSettings>({});
	const { generateDefaultValues, prepareScheme } = useDynamicWizard();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const { getWizardStructure } = useWizardTree({
		wizardKey,
		params: {
			userId: patientId,
		},
	});

	const loadWizard = async () => {
		setIsWizardLoading(true);
		const wizard = await getWizardStructure();
		const preparedScheme = prepareScheme(wizard.children);
		setScheme(preparedScheme);
		setDefaultValues(generateDefaultValues(preparedScheme));
		setWizardSettings(wizard?.settings);
		setIsWizardLoading(false);
	};

	useEffectAsync(async () => {
		await loadWizard();
	}, [wizardKey]);

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		try {
			const response = await apiClient.post(
				`wizard/${wizardKey}/submit?userId=${patientId}`,
				values,
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('base.saved'),
				});
				await loadWizard();
				onSuccess?.(wizardKey);
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isWizardLoading) {
		return <Loading />;
	}

	return (
		<ScrollContainer>
			<div className={styles.wrapper}>
				<MagicForm
					formScheme={scheme}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					showCharCounter={wizardSettings?.showCharCounter || true}
					disableSubmit={wizardSettings?.disabled || false}
					submitText={wizardSettings?.submitButtonTitle}
					readOnly={wizardSettings?.disabled || false}
				/>
			</div>
		</ScrollContainer>
	);
};

export default DynamicForm;
